import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  IconButton,
  Box,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  postEmployeeBankDetails,
  getEmployeeBankDetails,
  putEmployeeBankDetails,
  deleteEmployeeBankDetails,
  approvedEmployeeBankDetails,
} from "app/services/Services";
import { useParams } from "react-router-dom";
import {
  generateErrorMessage,
  generateSuccessMessage,
} from "app/utils/GenerateMessage";
import ConfirmModal from "./ConfirmModal";
import {
  T,
  sectionCardSx,
  muiFieldSx,
  SectionHeader,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  LoadingBlock,
  ErrorBlock,
} from "./shared/formKit";

const bankDetailsSchema = Yup.object().shape({
  bankDetails: Yup.array()
    .of(
      Yup.object().shape({
        bankName: Yup.string().required("Bank Name is required"),
        accountNumber: Yup.string()
          .matches(/^[0-9]{9,18}$/, "Enter a valid account number")
          .required("Account Number is required"),
        ifscCode: Yup.string()
          .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Enter a valid IFSC code")
          .required("IFSC Code is required"),
        branchName: Yup.string().required("Branch Name is required"),
        panCardNo: Yup.string()
          .required("PAN number is required")
          .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "PAN number must be valid and in uppercase"),
      })
    )
    .min(1, "At least one bank account is required")
    .max(2, "Maximum two bank accounts are allowed"),
});

const BankDetails = ({ onCompleted = null }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [existingBankDetails, setExistingBankDetails] = useState([]);
  const [employeeId] = useState(id);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const role = JSON.parse(localStorage.getItem("role"));

  const isEdit = existingBankDetails.length > 0;

  useEffect(() => {
    loadExistingBankDetails();
  }, [id]);

  const loadExistingBankDetails = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await getEmployeeBankDetails(id);
      if (response?.status === 200) {
        setExistingBankDetails(response.data?.data || []);
      } else {
        setExistingBankDetails([]);
      }
    } catch (err) {
      console.error("Error loading bank details:", err);
      setExistingBankDetails([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getInitialValues = () => {
    if (isEdit) {
      return {
        bankDetails: existingBankDetails.map((b) => ({
          id: b.id,
          bankName: b.bankName || "",
          accountNumber: b.accountNumber || "",
          ifscCode: b.ifscCode || "",
          branchName: b.branchName || "",
          panCardNo: b.panCardNo || "",
          isActive: b.isActive || false,
          isApprove: b.isApprove || false,
        })),
      };
    }
    return {
      bankDetails: [{ bankName: "", accountNumber: "", ifscCode: "", branchName: "", panCardNo: "" }],
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const updated = values.bankDetails.filter((b) => b.id);
      const created = values.bankDetails.filter((b) => !b.id);

      if (updated.length) {
        await putEmployeeBankDetails(employeeId, {
          bankDetails: updated.map((b) => ({
            bankId: b.id,
            bankName: b.bankName,
            accountNumber: b.accountNumber,
            ifscCode: b.ifscCode,
            branchName: b.branchName,
            panCardNo: b.panCardNo,
          })),
        });
      }

      if (created.length) {
        await postEmployeeBankDetails(employeeId, {
          employeeId,
          bankDetails: created.map((b) => ({
            bankName: b.bankName,
            accountNumber: b.accountNumber,
            ifscCode: b.ifscCode,
            branchName: b.branchName,
            panCardNo: b.panCardNo,
          })),
        });
      }

      generateSuccessMessage("Bank details saved successfully");
      await loadExistingBankDetails();
      if (onCompleted) onCompleted(values.bankDetails);
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || "Failed to save bank details");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (bankId, removeFromForm) => {
    if (!bankId) {
      removeFromForm();
      return;
    }
    setDeleteTarget({ bankId, removeFromForm });
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { bankId } = deleteTarget;

    try {
      setLoading(true);
      await deleteEmployeeBankDetails([bankId]);
      generateSuccessMessage("Bank details deleted successfully");
      await loadExistingBankDetails();
      setConfirmModalOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || "Failed to delete bank account");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBank = async (bankId) => {
    if (!bankId || !employeeId) return;
    try {
      setLoading(true);
      await approvedEmployeeBankDetails(employeeId, bankId);
      generateSuccessMessage("Bank account approved successfully");
      await loadExistingBankDetails();
    } catch (error) {
      generateErrorMessage(error.response?.data?.message || "Failed to approve bank account");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={sectionCardSx}>
        <LoadingBlock label="Loading bank details…" />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && <ErrorBlock message="Failed to load bank details." onRetry={loadExistingBankDetails} />}

      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={bankDetailsSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => {
          const canAddMore = values.bankDetails.length < 2;
          const showMaxRecordAlert = values.bankDetails.length >= 2;
          const newRecordsCount = values.bankDetails.filter((b) => !b.id).length;
          const hasUnsavedForm = newRecordsCount > 0;

          return (
            <Form>
              <FieldArray name="bankDetails">
                {({ push, remove }) => (
                  <Box sx={sectionCardSx}>
                    <SectionHeader
                      title={`Bank accounts (${values.bankDetails.length}/2)`}
                      sub="Up to two accounts. New accounts need HR approval before payroll uses them."
                      action={
                        <SecondaryButton
                          type="button"
                          onClick={() => push({ bankName: "", accountNumber: "", ifscCode: "", branchName: "", panCardNo: "" })}
                          disabled={!canAddMore || isSubmitting || hasUnsavedForm}
                        >
                          <AddIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: "6px" }} />
                          Add account
                        </SecondaryButton>
                      }
                    />

                    {showMaxRecordAlert && (
                      <Box sx={{ ...alertSx("neutral"), mb: 2 }}>
                        Maximum limit reached. Delete an existing account to add a new one.
                      </Box>
                    )}
                    {hasUnsavedForm && !showMaxRecordAlert && (
                      <Box sx={{ ...alertSx("warning"), mb: 2 }}>
                        Save or delete the current form before adding another bank account.
                      </Box>
                    )}

                    {values.bankDetails.map((bank, index) => (
                      <Box
                        key={`${bank.id || "new"}-${index}`}
                        sx={{
                          border: `1px solid ${T.cardBorder}`,
                          borderRadius: T.inputR,
                          padding: "16px 18px",
                          mb: "16px",
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Box sx={{ fontFamily: T.body_f, fontSize: "13.5px", fontWeight: 600, color: T.ink }}>
                              Account {index + 1}
                            </Box>
                            <StatusChip label={bank.id ? "Existing" : "New — please save"} tone={bank.id ? "neutral" : "warning"} />
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            {bank.id && (
                              <StatusChip label={bank.isActive ? "Active" : "Inactive"} tone={bank.isActive ? "success" : "neutral"} />
                            )}
                            {bank.id && (
                              bank.isApprove ? (
                                <StatusChip label="Approved" tone="success" />
                              ) : (
                                role?.roleName !== "EMPLOYEE" && (
                                  <SecondaryButton
                                    type="button"
                                    onClick={() => handleApproveBank(bank.id)}
                                    disabled={isSubmitting || loading}
                                    sx={{ padding: "6px 12px", fontSize: "12.5px" }}
                                  >
                                    Approve
                                  </SecondaryButton>
                                )
                              )
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(bank.id, () => remove(index))}
                              disabled={isSubmitting || loading}
                              sx={{ color: T.danger }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth size="small" sx={muiFieldSx}
                              name={`bankDetails.${index}.bankName`} label="Bank name"
                              value={bank.bankName} onChange={handleChange} onBlur={handleBlur}
                              error={touched.bankDetails?.[index]?.bankName && Boolean(errors.bankDetails?.[index]?.bankName)}
                              helperText={touched.bankDetails?.[index]?.bankName && errors.bankDetails?.[index]?.bankName}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth size="small" sx={muiFieldSx}
                              name={`bankDetails.${index}.accountNumber`} label="Account number"
                              value={bank.accountNumber} onChange={handleChange} onBlur={handleBlur}
                              error={touched.bankDetails?.[index]?.accountNumber && Boolean(errors.bankDetails?.[index]?.accountNumber)}
                              helperText={touched.bankDetails?.[index]?.accountNumber && errors.bankDetails?.[index]?.accountNumber}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth size="small" sx={muiFieldSx}
                              name={`bankDetails.${index}.ifscCode`} label="IFSC code" placeholder="e.g. SBIN0001234"
                              value={bank.ifscCode} onChange={handleChange} onBlur={handleBlur}
                              error={touched.bankDetails?.[index]?.ifscCode && Boolean(errors.bankDetails?.[index]?.ifscCode)}
                              helperText={touched.bankDetails?.[index]?.ifscCode && errors.bankDetails?.[index]?.ifscCode}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth size="small" sx={muiFieldSx}
                              name={`bankDetails.${index}.branchName`} label="Branch name"
                              value={bank.branchName} onChange={handleChange} onBlur={handleBlur}
                              error={touched.bankDetails?.[index]?.branchName && Boolean(errors.bankDetails?.[index]?.branchName)}
                              helperText={touched.bankDetails?.[index]?.branchName && errors.bankDetails?.[index]?.branchName}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth size="small" sx={muiFieldSx}
                              name={`bankDetails.${index}.panCardNo`} label="PAN card no."
                              value={bank.panCardNo?.toUpperCase() || ""}
                              onChange={(e) => { e.target.value = e.target.value.toUpperCase(); handleChange(e); }}
                              onBlur={handleBlur}
                              inputProps={{ style: { textTransform: "uppercase" } }}
                              error={touched.bankDetails?.[index]?.panCardNo && Boolean(errors.bankDetails?.[index]?.panCardNo)}
                              helperText={touched.bankDetails?.[index]?.panCardNo && errors.bankDetails?.[index]?.panCardNo}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    ))}

                    {typeof errors.bankDetails === "string" && (
                      <FormHelperText error>{errors.bankDetails}</FormHelperText>
                    )}

                    <Box mt={2} display="flex" gap={2}>
                      <PrimaryButton disabled={isSubmitting || !values.bankDetails.length}>
                        {isSubmitting ? "Saving…" : "Save / update"}
                      </PrimaryButton>
                      <SecondaryButton type="button" onClick={loadExistingBankDetails} disabled={isSubmitting}>
                        Refresh
                      </SecondaryButton>
                    </Box>
                  </Box>
                )}
              </FieldArray>
            </Form>
          );
        }}
      </Formik>

      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleConfirmDelete}
        title="Delete Bank Account?"
        description="Are you sure you want to delete this bank account? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />
    </Box>
  );
};

function alertSx(tone) {
  const tones = {
    warning: { bg: T.warningBg, color: T.warningText },
    neutral: { bg: T.hairline, color: T.body2 },
  };
  const c = tones[tone] || tones.neutral;
  return {
    padding: "10px 14px",
    borderRadius: T.inputR,
    background: c.bg,
    color: c.color,
    fontFamily: T.body_f,
    fontSize: "13px",
  };
}

export default BankDetails;
